import { AxiosRequestConfig, AxiosInstance } from 'axios';
import { ConsultEntity } from '@tencent/dz-models';
import axios from 'src/api/axios';
import { logger } from './Logger';

interface Options {
  citoken: string;
  requestInstance?: any
}

type RequestConfig = AxiosRequestConfig & {
  updateRemoteCiTokenFail?: (error: any) => void
  updateRemoteCiTokenSuccess?: (data: any) => void
};

export interface Response<T = any> {
  code: number;
  data: T;
  message: string;
}

/**
 * 封装这个请求类，主要是因为citoken 需要在有效期内才能请求，
 * 为了减少在每次请求都验证citoken这里统一封装
 */

export class Request {
  // 所有接口需要保证citoken在有效期
  private citoken: string;
  private consult!: ConsultEntity;
  private requestInstance!: AxiosInstance;
  // 5分钟过期
  private citokenExpireTime = 300 * 1000;

  constructor(options: Options, consult: ConsultEntity) {
    this.citoken = options.citoken;
    this.consult = consult;
    this.requestInstance = axios.axiosLoader(this.citoken);
  }

  public isEffectiveCitoken() {
    if (!this.citoken) return false;

    const [time] = this.citoken.split('-');

    const citokenCreateTime = Number(`${time}000`);

    const currentTime = new Date().getTime();

    return currentTime - citokenCreateTime < this.citokenExpireTime;
  }

  public async updateRemoteCiToken({
    updateRemoteCiTokenFail,
    updateRemoteCiTokenSuccess,
  }: Partial<Pick<RequestConfig, 'updateRemoteCiTokenFail' | 'updateRemoteCiTokenSuccess'>>,
  ) {
    // citoken有效
    if (this.isEffectiveCitoken()) return;

    try {
      const url = (this.consult as any).use_new_interface_flag ? '/viewer/common/token/get_ci_token' : '/common/token/getcitoken';
      const response = await this.privateGet(url);

      if (response.code !== 0) throw response;

      const { data } = response;
      window.FMC_HOST.citoken = data.citoken;
      axios.updateCitoken(data.citoken);
      updateRemoteCiTokenSuccess?.(data);
    } catch (error) {
      updateRemoteCiTokenFail?.(error);
      logger.error('getcitoken error', error);
      throw error;
    }
  }

  public combinedRequest = <D = any>(
    method: 'get' | 'post',
    url: string,
    dataOrConfig?: D | AxiosRequestConfig,
    config?: RequestConfig,
  ): Promise<Response<D>> => {
    return new Promise(async (resolve, reject) => {
      try {
        await this.updateRemoteCiToken({
          updateRemoteCiTokenFail: config?.updateRemoteCiTokenFail,
          updateRemoteCiTokenSuccess: config?.updateRemoteCiTokenSuccess,
        });

        let result: unknown;
        if (method === 'get') {
          result = await this.requestInstance.get(url, dataOrConfig as any);
        } else if (method === 'post') {
          result = await this.requestInstance.post(url, dataOrConfig);
        }
        resolve(result as Response<D>);
      } catch (error) {
        reject(error);
      }
    });
  };

  public get = <D = any>(url: string, config?: RequestConfig) => {
    return this.combinedRequest<D>('get', url, config);
  };

  public post = <D = any>(url: string, data?: any, config?: RequestConfig) => {
    return this.combinedRequest<D>('post', url, data, config);
  };

  public postForm = <D = any>(url: string, data?: any, config?: RequestConfig) => {
    return this.combinedRequest<D>('post', url, data, config);
  };

  public privateGet = <D = any>(url: string, config?: AxiosRequestConfig): Promise<Response<D>> => {
    return this.requestInstance.get(url, config)
      .then(res => res)
      .catch(err => err);
  };

  public privateGetPost = <D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<Response<D>> => {
    return this.requestInstance.post<D>(url, data, config)
      .then(res => res)
      .catch(err => err);
  };
}
